import { useState, useMemo, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { Spinner } from '@/components/ui/Spinner'
import { useInvoices } from '@/hooks/useInvoices'
import { useContacts } from '@/hooks/useContacts'
import { supabase } from '@/lib/supabase'
import { formatCurrency, generateId } from '@/lib/utils'
import type { InvoiceLine, CreateInvoiceData } from '@/types/invoice'
import { ProductSelect } from '@/components/admin/ProductSelect'
import type { Produit } from '@/types/database-real'
import {
  Plus,
  Trash2,
  ChevronLeft,
  Save,
  Send,
  User,
  Search,
  Building2,
  Mail,
} from 'lucide-react'
import toast from 'react-hot-toast'

// Add days helper
function addDays(date: Date, days: number): Date {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

// Format date for input
function formatDateForInput(date: Date): string {
  return date.toISOString().split('T')[0]
}

// TVA options
const tvaOptions = [
  { value: 20, label: '20%' },
  { value: 10, label: '10%' },
  { value: 5.5, label: '5.5%' },
  { value: 0, label: '0%' },
]

// Contact selection modal (same style as NewQuotePage)
interface ContactSelectModalProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (contactId: string) => void
}

function ContactSelectModal({ isOpen, onClose, onSelect }: ContactSelectModalProps) {
  const [search, setSearch] = useState('')
  const { contacts, isLoading } = useContacts({ search })

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Selectionner un client" size="lg">
      <div className="space-y-4">
        <Input
          placeholder="Rechercher un contact..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          leftIcon={<Search className="w-4 h-4" />}
        />

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-8 text-brown-500">
            <User className="w-8 h-8 mx-auto mb-2 text-beige-300" />
            <p>Aucun contact trouve</p>
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto space-y-2">
            {contacts.map((contact) => (
              <button
                key={contact.id}
                onClick={() => {
                  onSelect(contact.id)
                  onClose()
                }}
                className="w-full p-3 rounded-lg border border-beige-200 hover:border-mint-500 hover:bg-mint-50 transition-colors text-left flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-full bg-mint-100 flex items-center justify-center">
                  <span className="text-mint-700 font-semibold">
                    {contact.prenom?.charAt(0) || ''}{contact.nom?.charAt(0) || ''}
                  </span>
                </div>
                <div className="flex-1">
                  <div className="font-medium text-brown-900">
                    {contact.prenom} {contact.nom}
                  </div>
                  <div className="text-sm text-brown-500 flex items-center gap-2">
                    {contact.entreprise && (
                      <span className="flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {contact.entreprise}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {contact.email}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </Modal>
  )
}

// Empty line template
function createEmptyLine(): InvoiceLine {
  return {
    id: generateId(),
    description: '',
    quantite: 1,
    prix_unitaire: 0,
    taux_tva: 0,
    total_ht: 0,
  }
}

export default function NewInvoicePage() {
  const navigate = useNavigate()
  const { createInvoice, isCreating } = useInvoices()
  const { contacts } = useContacts()

  // UI state
  const [showContactModal, setShowContactModal] = useState(false)

  // Form state
  const [clientId, setClientId] = useState('')
  const [projetId, setProjetId] = useState('')
  const [projects, setProjects] = useState<Array<{ id: string; title: string }>>([])
  const [loadingProjects, setLoadingProjects] = useState(false)
  const [lignes, setLignes] = useState<InvoiceLine[]>([createEmptyLine()])
  const [dateCreation] = useState(formatDateForInput(new Date()))
  const [dateEcheance, setDateEcheance] = useState(
    formatDateForInput(addDays(new Date(), 15))
  )
  const [conditions, setConditions] = useState(
    'Paiement a reception de facture sous 15 jours.'
  )
  const [notes, setNotes] = useState('')

  // Load projects when client changes
  useEffect(() => {
    if (clientId) {
      setLoadingProjects(true)
      supabase
        .from('projects')
        .select('id, title, status')
        .eq('user_id', clientId)
        .order('created_at', { ascending: false })
        .then(({ data, error }) => {
          if (error) {
            console.error('Error loading projects:', error)
            toast.error('Erreur lors du chargement des projets')
          } else {
            setProjects(data || [])
          }
          setLoadingProjects(false)
        })
    } else {
      setProjects([])
      setProjetId('')
    }
  }, [clientId])

  // Calculate totals
  const totaux = useMemo(() => {
    const totalHT = lignes.reduce((sum, l) => sum + l.total_ht, 0)
    const tvaByRate: Record<number, number> = {}

    lignes.forEach((l) => {
      if (l.total_ht > 0) {
        const tvaAmount = l.total_ht * l.taux_tva / 100
        tvaByRate[l.taux_tva] = (tvaByRate[l.taux_tva] || 0) + tvaAmount
      }
    })

    const totalTVA = Object.values(tvaByRate).reduce((sum, v) => sum + v, 0)

    return {
      totalHT,
      tvaByRate,
      totalTVA,
      totalTTC: totalHT + totalTVA,
    }
  }, [lignes])

  // Get selected contact
  const selectedContact = useMemo(() => {
    return contacts.find((c) => c.id === clientId)
  }, [contacts, clientId])

  // Add a new empty line
  const addLigne = () => {
    setLignes([...lignes, createEmptyLine()])
  }

  // Update a line
  const updateLigne = (id: string, field: keyof InvoiceLine, value: string | number) => {
    setLignes(
      lignes.map((l) => {
        if (l.id !== id) return l
        const updated = { ...l, [field]: value }
        // Recalculate total_ht when quantity or price changes
        if (field === 'quantite' || field === 'prix_unitaire') {
          updated.total_ht = updated.quantite * updated.prix_unitaire
        }
        return updated
      })
    )
  }

  // Select a product for a line
  const handleProductSelect = (id: string, produit: Produit | null) => {
    if (!produit) return

    setLignes(
      lignes.map((l) => {
        if (l.id !== id) return l
        return {
          ...l,
          description: produit.nom + (produit.description ? ` - ${produit.description}` : ''),
          prix_unitaire: produit.prix_unitaire,
          taux_tva: produit.tva,
          total_ht: l.quantite * produit.prix_unitaire,
        }
      })
    )
  }

  // Remove a line
  const removeLigne = (id: string) => {
    if (lignes.length === 1) return
    setLignes(lignes.filter((l) => l.id !== id))
  }

  // Form validation
  const isValid = useMemo(() => {
    if (!clientId) return false
    if (lignes.every((l) => l.total_ht === 0)) return false
    if (lignes.some((l) => l.description.trim() === '' && l.total_ht > 0)) return false
    return true
  }, [clientId, lignes])

  // Submit handler
  const handleSubmit = async (sendNow: boolean) => {
    if (!isValid) {
      if (!clientId) {
        toast.error('Veuillez selectionner un client')
      } else {
        toast.error('Veuillez ajouter au moins une ligne valide')
      }
      return
    }

    try {
      const data: CreateInvoiceData = {
        utilisateur_id: clientId,
        projet_id: projetId || undefined,
        date_creation: dateCreation,
        date_echeance: dateEcheance,
        lignes: lignes.filter((l) => l.total_ht > 0),
        conditions,
        notes_internes: notes || undefined,
        statut: sendNow ? 'sent' : 'draft',
      }

      const invoice = await createInvoice(data)
      toast.success(sendNow ? 'Facture creee et envoyee' : 'Brouillon enregistre')
      navigate(`/invoices/${invoice.id}`)
    } catch {
      toast.error('Erreur lors de la creation de la facture')
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <Link
          to="/invoices"
          className="text-brown-500 hover:text-brown-700 flex items-center gap-1"
        >
          <ChevronLeft className="w-4 h-4" />
          Factures
        </Link>
        <span className="text-brown-400">/</span>
        <span className="text-brown-700">Nouvelle facture</span>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Nouvelle facture</CardTitle>
        </CardHeader>

        <div className="p-6 pt-0 space-y-6">
          {/* Client selection */}
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-2">
              Client *
            </label>
            {selectedContact ? (
              <div className="flex items-center justify-between p-4 bg-beige-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-mint-100 flex items-center justify-center">
                    <span className="text-mint-700 font-semibold">
                      {selectedContact.prenom?.charAt(0) || ''}{selectedContact.nom?.charAt(0) || ''}
                    </span>
                  </div>
                  <div>
                    <div className="font-medium text-brown-900">
                      {selectedContact.prenom} {selectedContact.nom}
                    </div>
                    <div className="text-sm text-brown-500">
                      {selectedContact.email}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowContactModal(true)}
                >
                  Changer
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => setShowContactModal(true)}
                leftIcon={<User className="w-4 h-4" />}
                className="w-full"
              >
                Selectionner un client
              </Button>
            )}
          </div>

          {/* Projet associe */}
          {clientId && projects.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-brown-700 mb-1">
                Projet associe
              </label>
              <select
                value={projetId}
                onChange={(e) => setProjetId(e.target.value)}
                className="w-full px-4 py-2 bg-white border border-beige-300 rounded-lg text-brown-900 focus:outline-none focus:ring-2 focus:ring-mint-500 focus:border-transparent"
              >
                <option value="">Aucun projet</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.title}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Date de creation"
              type="date"
              value={dateCreation}
              disabled
            />
            <Input
              label="Date d'echeance"
              type="date"
              value={dateEcheance}
              onChange={(e) => setDateEcheance(e.target.value)}
            />
          </div>

          {/* Invoice lines */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-brown-700">
                Lignes de facture
              </label>
              <Button variant="outline" size="sm" onClick={addLigne}>
                <Plus className="w-4 h-4 mr-1" />
                Ajouter une ligne
              </Button>
            </div>

            <div className="space-y-3">
              {lignes.map((ligne, index) => (
                <div
                  key={ligne.id}
                  className="p-4 border border-beige-200 rounded-lg bg-beige-50 space-y-3"
                >
                  {/* Product Select */}
                  <ProductSelect
                    value={undefined}
                    onChange={(produit) => handleProductSelect(ligne.id, produit)}
                  />

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-brown-700 mb-1">
                      Description (modifiable)
                    </label>
                    <input
                      type="text"
                      value={ligne.description}
                      onChange={(e) =>
                        updateLigne(ligne.id, 'description', e.target.value)
                      }
                      placeholder="Description du service..."
                      className="w-full px-3 py-2 border border-beige-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-mint-500"
                    />
                  </div>

                  {/* Quantity, Price, TVA, Total */}
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-brown-700 mb-1">
                        Quantité
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={ligne.quantite}
                        onChange={(e) =>
                          updateLigne(ligne.id, 'quantite', Number(e.target.value))
                        }
                        className="w-full px-3 py-2 border border-beige-200 rounded-lg text-sm text-right focus:outline-none focus:ring-2 focus:ring-mint-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brown-700 mb-1">
                        Prix HT
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={ligne.prix_unitaire}
                        onChange={(e) =>
                          updateLigne(ligne.id, 'prix_unitaire', Number(e.target.value))
                        }
                        className="w-full px-3 py-2 border border-beige-200 rounded-lg text-sm text-right focus:outline-none focus:ring-2 focus:ring-mint-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brown-700 mb-1">
                        TVA
                      </label>
                      <select
                        value={ligne.taux_tva}
                        onChange={(e) =>
                          updateLigne(ligne.id, 'taux_tva', Number(e.target.value))
                        }
                        className="w-full px-3 py-2 border border-beige-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-mint-500"
                      >
                        {tvaOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brown-700 mb-1">
                        Total HT
                      </label>
                      <div className="px-3 py-2 bg-white border border-beige-200 rounded-lg text-sm font-semibold text-brown-900 text-right">
                        {formatCurrency(ligne.total_ht)}
                      </div>
                    </div>
                  </div>

                  {/* Delete button */}
                  {lignes.length > 1 && (
                    <div className="flex justify-end">
                      <button
                        onClick={() => removeLigne(ligne.id)}
                        className="flex items-center gap-1 px-3 py-1 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Supprimer cette ligne
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-72 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-brown-600">Sous-total HT</span>
                <span className="font-medium">{formatCurrency(totaux.totalHT)}</span>
              </div>
              {Object.entries(totaux.tvaByRate).map(([rate, amount]) => (
                <div key={rate} className="flex justify-between text-sm">
                  <span className="text-brown-500">TVA {rate}%</span>
                  <span>{formatCurrency(amount)}</span>
                </div>
              ))}
              <div className="flex justify-between pt-2 border-t border-beige-200 text-lg font-bold">
                <span>Total TTC</span>
                <span className="text-mint-600">{formatCurrency(totaux.totalTTC)}</span>
              </div>
            </div>
          </div>

          {/* Conditions */}
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-1">
              Conditions de paiement
            </label>
            <textarea
              value={conditions}
              onChange={(e) => setConditions(e.target.value)}
              rows={2}
              className="w-full px-4 py-2 bg-white border border-beige-300 rounded-lg text-brown-900 placeholder-brown-400 focus:outline-none focus:ring-2 focus:ring-mint-500 focus:border-transparent"
            />
          </div>

          {/* Internal notes */}
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-1">
              Notes internes (non visibles sur la facture)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Notes pour votre usage..."
              className="w-full px-4 py-2 bg-white border border-beige-300 rounded-lg text-brown-900 placeholder-brown-400 focus:outline-none focus:ring-2 focus:ring-mint-500 focus:border-transparent"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-beige-200">
            <Button variant="outline" onClick={() => navigate('/invoices')}>
              Annuler
            </Button>
            <Button
              variant="secondary"
              onClick={() => handleSubmit(false)}
              isLoading={isCreating}
              leftIcon={<Save className="w-4 h-4" />}
            >
              Enregistrer brouillon
            </Button>
            <Button
              onClick={() => handleSubmit(true)}
              isLoading={isCreating}
              leftIcon={<Send className="w-4 h-4" />}
            >
              Creer et envoyer
            </Button>
          </div>
        </div>
      </Card>

      {/* Contact selection modal */}
      <ContactSelectModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        onSelect={(contactId) => setClientId(contactId)}
      />
    </div>
  )
}
